import { useEffect, useRef } from 'react';
import { abortOnly, ABORT_KEYS } from 'core/slices/utils/abortManager';

// Persists the last successfully-triggered context key across TasksView unmounts.
// Set optimistically when a load is triggered — StrictMode-safe because setting
// it to the same value on double-invoke is idempotent.
let persistedLoadedKey = null;

/**
 * Triggers a full data reload whenever the store, task type, or filters change.
 * Resets pagination and re-fetches tasks, progress, aisles, and brands in parallel.
 * Calls onReloadStart before fetching to let the parent reset its local state.
 * On unmount, cancels all in-flight task requests and invalidates the key so
 * re-mounting always triggers a fresh reload.
 */
export function useTasksReload({
  currentStore,
  currentTaskType,
  filters,
  clearTaskList,
  loadTaskList,
  getAislesByTasks,
  getResourcesOverhead,
  getResourcesTaskDetailsFeatures,
  getTaskList,
  getTasksProgress,
  getAllBrands,
  getTasksStatus,
  onReloadStart,
}) {
  useEffect(() => {
    if (!currentStore || !currentTaskType) return;
    getTasksStatus(currentTaskType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, currentTaskType]);

  // On unmount: cancel in-flight requests and invalidate the cached key so that
  // re-mounting always triggers a fresh list reload (even if store/type/filters
  // haven't changed — the user navigated away and back, data may be stale).
  useEffect(() => {
    return () => {
      persistedLoadedKey = null;
      abortOnly(ABORT_KEYS.TASK_LIST);
      abortOnly(ABORT_KEYS.TASK_LIST_SEARCH);
      abortOnly(ABORT_KEYS.TASKS_PROGRESS);
      abortOnly(ABORT_KEYS.TASK_AISLES);
    };
  }, []);

  const onReloadStartRef = useRef(onReloadStart);
  useEffect(() => { onReloadStartRef.current = onReloadStart; });

  useEffect(() => {
    if (!currentStore || !currentTaskType) return;

    const currentKey = `${currentStore}|${currentTaskType}|${JSON.stringify(filters)}`;

    if (persistedLoadedKey === currentKey) return;

    // Set optimistically before fetching — idempotent on StrictMode double-invoke
    persistedLoadedKey = currentKey;

    onReloadStartRef.current?.();

    clearTaskList();
    loadTaskList();
    // The header counter reads the progress totals, and those are filter-scoped,
    // so they have to be refetched with the list on every filter change.
    getTasksProgress();
    getAislesByTasks();
    getResourcesOverhead();
    // Fetched with the list, not with the task detail: by the time a task is
    // opened the answer is already in the store, so an opted-in chain never
    // shows the barcode slide first and then swaps it for a button.
    getResourcesTaskDetailsFeatures();
    getTaskList(0);
    getAllBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore, currentTaskType, filters]);
}
